import {
  Alert,
  Link as ExternalLink,
  Heading,
  Paragraph,
  Skeleton,
} from "@digdir/designsystemet-react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { httpClient } from "@/api/client";
import type { MålerdataOpplastingerStatus } from "@/api/generated";
import { getOppgjørForPeriodeOptions } from "@/api/generated/@tanstack/react-query.gen";
import type { InternLenke } from "@/components/LenkerContainer";
import { DokumentasjonsLenker } from "@/features/dokumentasjon/DokumentasjonsLenker";
import { cn } from "@/utils";
import {
  formatDateInterval,
  getMonthStringFromDate,
} from "@/utils/dateTimeUtils";
import { SeparateOppgjorInfo } from "../../shared/SeparateOppgjorInfo";
import { VeiledningLenke } from "../../shared/VeiledningLenke";
import { SimuleringsområdeLenke } from "../simulering/SimuleringsområdeLenke";
import { TestområdeLenke } from "../validering/TestområdeLenke";
import { InnrapporteringsFrist } from "./InnrapporteringsFrist";
import { Kjøreplaner } from "./Kjøreplaner";
import { MålerdataGrunnlag } from "./MaaledataPeriodeGrunnlag";
import { MaaledataPeriodeOppsummering } from "./MaaledataPeriodeOppsummering";
import { OpplastingStatus } from "./OpplastingStatus";

export const MaaledataPeriode = ({
  organisasjonsNummer,
  month,
  year,
  className,
}: {
  organisasjonsNummer: number;
  month: number;
  year: number;
  className?: string;
}) => {
  const { t } = useTranslation();

  const { data, isLoading, isError } = useQuery({
    ...getOppgjørForPeriodeOptions({
      path: {
        organisasjonsNummer: organisasjonsNummer,
      },
      query: {
        month,
        year,
      },
      client: httpClient,
    }),
  });

  const fromInclusive = new Date(year, month - 1, 1);
  const toExclusive = new Date(fromInclusive);
  toExclusive.setMonth(toExclusive.getMonth() + 1);

  const avtalerEksportLenke: InternLenke = {
    href: "/dokumentasjon/innrapportering",
    text: t("pages.dokumentasjon.lenker.innrapportering"),
    external: false,
  };

  return (
    <div
      className={cn(
        "flex flex-col gap-4 xl:flex-row xl:justify-between",
        className,
      )}
    >
      <div className="max-w-card space-y-5">
        <PeriodeHeader
          className="mb-3 space-y-5"
          fromInclusive={fromInclusive}
          toExclusive={toExclusive}
          opplastingStatus={data?.opplastingStatus}
        />
        {isLoading ? (
          <div>
            <Skeleton width={"100%"} height={200} />
          </div>
        ) : isError ? (
          <div>
            <Alert data-color="danger">
              {t("pages.maaledata-periode.feil.laste-feil")}
            </Alert>
          </div>
        ) : (
          <>
            {data ? (
              <MaaledataPeriodeOppsummering data={data} className="mb-9" />
            ) : (
              <Paragraph className="text-lg mt-5">
                {t("pages.maaledata-periode.ikke-lastet-opp-data")}
              </Paragraph>
            )}
            <MålerdataGrunnlag
              className="max-w-card mt-9"
              oppgjør={data || null} // Can be empty string if no data
              organisasjonsNummer={organisasjonsNummer}
              year={year}
              month={month}
              opplastingStatus={data?.opplastingStatus}
            />
            <InnrapporteringsFrist
              month={month}
              year={year}
              målerdataOpplastingerStatus={data?.opplastingStatus}
            />
            <Kjøreplaner />
            <SeparateOppgjorInfo className="mt-9" />
            <div className="space-y-3 pt-5">
              <Heading level={3} data-size="xs">
                {t("pages.maaledata-periode.grunnlag.tekniske-problem.tittel")}
              </Heading>
              <Paragraph className="text-pretty whitespace-pre-line">
                <ExternalLink asChild>
                  <Link to={"/veiledning"} target="_blank" rel="noreferrer">
                    <span>
                      {t(
                        "pages.maaledata-periode.grunnlag.tekniske-problem.veileder",
                      )}
                    </span>
                  </Link>
                </ExternalLink>
                {". "}
                {t(
                  "pages.maaledata-periode.grunnlag.tekniske-problem.beskrivelse",
                )}
                <ExternalLink
                  href="mailto:teamenergi@enova.no"
                  target="_blank"
                  rel="noreferrer"
                >
                  teamenergi@enova.no
                </ExternalLink>
              </Paragraph>
            </div>
          </>
        )}
      </div>
      <div className="max-w-small-card space-y-6">
        <VeiledningLenke />
        <TestområdeLenke />
        <SimuleringsområdeLenke />
        <DokumentasjonsLenker
          lenker={[avtalerEksportLenke]}
          className="w-full"
        />
      </div>
    </div>
  );
};

const PeriodeHeader = ({
  fromInclusive,
  toExclusive,
  opplastingStatus,
  className,
}: {
  fromInclusive: Date;
  toExclusive: Date;
  opplastingStatus?: MålerdataOpplastingerStatus | null;
  className?: string;
}) => {
  const { t } = useTranslation();

  return (
    <div className={className}>
      <div className="flex flex-col gap-2">
        <Heading level={2} data-size={"md"}>
          {t("pages.maaledata-periode.tittel", {
            context: getMonthStringFromDate(new Date(fromInclusive)),
          })}{" "}
          {new Date(fromInclusive).getFullYear()}
        </Heading>
        <OpplastingStatus opplastingStatus={opplastingStatus ?? null} />
      </div>
      <span className="text-balder-text-subtle">
        {t("pages.maaledata-periode.periode")}
        {formatDateInterval(fromInclusive, toExclusive)}
      </span>
    </div>
  );
};
